/**
 * The small dialogs of Cài đặt › Module: add a connection, apply (or send for approval) a tested
 * draft, switch a connection off or on. Every write carries a reason and an idempotency key.
 */

import { Power, PowerOff, Send } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { IntegrationEnvironment } from '../../api/areas/integrations';
import type { CreateModuleInstanceBody, ModuleChangeResult, ModuleField, ModuleInstance, ModuleManifest, ModuleVersion } from '../../api/areas/modules';
import { Callout, Field } from '../../components/blocks';
import { useWrite } from '../../components/Write';
import { ConfirmCheck, EnvironmentBanner, ReasonField, WriteOutcome } from '../integrations/shared';
import { isWriteOnly } from './fieldKinds';
import { ModuleError, modulesUrl, useInstanceLabel, useText, useValueText } from './ui';

const KEY = /^[a-z0-9][a-z0-9-]{0,39}$/;

/** "Thêm kết nối": a new instance of a module. */
export function CreateInstanceForm({ manifest, first, env, onClose, onDone }: { manifest: ModuleManifest; first: boolean; env: IntegrationEnvironment; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('modules');
  const [key, setKey] = useState(first ? 'default' : '');
  const [displayName, setDisplayName] = useState('');
  const [reason, setReason] = useState('');
  const write = useWrite((body: CreateModuleInstanceBody, opts) => api.createModuleInstance(manifest.branch, manifest.id, body, opts));
  const keyOk = KEY.test(key.trim());
  if (write.state.phase === 'done') {
    const created = write.state.result.item;
    return (
      <div className="space-y-4" role="status">
        <Callout tone="ok">{t('create.created', { key: created.key })}</Callout>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('form.close')}
          </Button>
          <Button asChild>
            <Link to={modulesUrl(manifest.branch, manifest.id, created.key, 'config')} onClick={onClose}>
              {t('create.open')}
            </Link>
          </Button>
        </DialogFooter>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      <Field label={t('create.key')} hint={keyOk || !key ? t('create.keyHint') : <span className="text-critical">{t('errors.field.INVALID_KEY')}</span>}>
        <Input value={key} onChange={(e) => setKey(e.target.value.toLowerCase())} className="font-mono" maxLength={40} autoComplete="off" spellCheck={false} />
      </Field>
      <Field label={t('create.displayName')} hint={t('form.displayNameHint')}>
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={100} />
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      {write.state.phase === 'error' ? <ModuleError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('form.cancel')}
        </Button>
        <Button
          type="button"
          disabled={!keyOk || !reason.trim() || write.busy}
          onClick={() =>
            void write.run({ key: key.trim(), display_name: displayName.trim() || null, reason: reason.trim() }).then((r) => {
              if (r) onDone();
            })
          }
        >
          {t('card.add')}
        </Button>
      </DialogFooter>
    </div>
  );
}

/** The result of applying, switching off or on: applied, waiting for a second person, or to test first. */
function ChangeOutcome({ result, env, onClose }: { result: ModuleChangeResult; env: IntegrationEnvironment; onClose: () => void }) {
  const { t } = useTranslation('modules');
  return (
    <div className="space-y-4">
      {result.result === 'TEST_REQUIRED' ? (
        <Callout tone="info" title={t('status.DRAFT')}>
          {t('toggle.testRequired')}
        </Callout>
      ) : (
        <WriteOutcome result={result.result} approval={result.approval} warnings={[]} env={env} />
      )}
      <DialogFooter>
        <Button onClick={onClose}>{t('form.close')}</Button>
      </DialogFooter>
    </div>
  );
}

/** The fields whose value differs between the version in force and the draft. */
function DraftChanges({ draft, active, fields }: { draft: ModuleVersion; active: ModuleVersion | null; fields: readonly ModuleField[] }) {
  const { t } = useTranslation('modules');
  const text = useText();
  const valueText = useValueText();
  const rows: { key: string; label: string; before: ReactNode; after: ReactNode }[] = [];
  if (!active || active.enabled !== draft.enabled) {
    rows.push({ key: 'enabled', label: t('config.enabledField'), before: active ? (active.enabled ? t('status.ACTIVE') : t('status.DISABLED')) : '—', after: draft.enabled ? t('status.ACTIVE') : t('status.DISABLED') });
  }
  for (const f of fields) {
    if (isWriteOnly(f.kind)) {
      const a = active?.secrets[f.key];
      const d = draft.secrets[f.key];
      if (!!a?.configured !== !!d?.configured || a?.changed_at !== d?.changed_at || a?.source !== d?.source) {
        rows.push({ key: f.key, label: text(f.label), before: a?.configured ? '••••' : '—', after: d?.configured ? '••••' : '—' });
      }
      continue;
    }
    const before = active?.values[f.key] ?? null;
    const after = draft.values[f.key] ?? null;
    if (JSON.stringify(before) !== JSON.stringify(after)) rows.push({ key: f.key, label: text(f.label), before: active ? valueText(f, before) : '—', after: valueText(f, after) });
  }
  if (!rows.length) return <p className="text-sm text-muted-foreground">{t('propose.noChanges')}</p>;
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">{t('propose.changes')}</p>
      <ul className="grid gap-1.5 text-sm">
        {rows.map((r) => (
          <li key={r.key} className="grid gap-0.5 rounded-md border p-2">
            <span className="text-xs font-medium text-muted-foreground">{r.label}</span>
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground line-through decoration-muted-foreground/50">{r.before}</span>
              <span aria-hidden="true">→</span>
              <span>{r.after}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "Áp dụng bản nháp" / "Gửi duyệt": a tested draft becomes the version in force (or waits for approval). */
export function ProposeForm({
  manifest,
  instance,
  draft,
  fields,
  env,
  onClose,
  onDone,
}: {
  manifest: ModuleManifest;
  instance: ModuleInstance;
  draft: ModuleVersion;
  fields: readonly ModuleField[];
  env: IntegrationEnvironment;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('modules');
  const [reason, setReason] = useState(draft.reason);
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: { reason: string }, opts) => api.proposeModuleVersion(manifest.branch, manifest.id, instance.key, draft.id, body, opts));
  const production = env === 'PRODUCTION';
  if (write.state.phase === 'done') return <ChangeOutcome result={write.state.result} env={env} onClose={onClose} />;
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      <p className="text-sm">{production ? t('propose.descriptionProduction') : t('propose.description')}</p>
      <DraftChanges draft={draft} active={instance.active} fields={fields} />
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {write.state.phase === 'error' ? <ModuleError error={write.state.error} fields={fields} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('form.cancel')}
        </Button>
        <Button
          type="button"
          variant={production ? 'destructive' : 'default'}
          disabled={!reason.trim() || !confirmed || write.busy}
          onClick={() =>
            void write.run({ reason: reason.trim() }).then((r) => {
              if (r) onDone();
            })
          }
        >
          <Send /> {production ? t('config.proposeProduction') : t('config.propose')}
        </Button>
      </DialogFooter>
    </div>
  );
}

/** "Bật" / "Tắt" a connection. */
export function ToggleForm({
  manifest,
  instance,
  on,
  env,
  onClose,
  onDone,
}: {
  manifest: ModuleManifest;
  instance: ModuleInstance;
  on: boolean;
  env: IntegrationEnvironment;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('modules');
  const label = useInstanceLabel();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: { reason: string }, opts) =>
    on ? api.enableModuleInstance(manifest.branch, manifest.id, instance.key, body, opts) : api.disableModuleInstance(manifest.branch, manifest.id, instance.key, body, opts),
  );
  const production = env === 'PRODUCTION';
  if (write.state.phase === 'done') return <ChangeOutcome result={write.state.result} env={env} onClose={onClose} />;
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      <p className="text-sm font-medium">{label(instance)}</p>
      <p className="text-sm">{on ? t('toggle.onDescription') : t('toggle.offDescription')}</p>
      {!on && production && manifest.disable === 'needs_approval' ? <Callout tone="warn">{t('toggle.offApproval')}</Callout> : null}
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {write.state.phase === 'error' ? <ModuleError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('form.cancel')}
        </Button>
        <Button
          type="button"
          variant={!on || production ? 'destructive' : 'default'}
          disabled={!reason.trim() || !confirmed || write.busy}
          onClick={() =>
            void write.run({ reason: reason.trim() }).then((r) => {
              if (r) onDone();
            })
          }
        >
          {on ? <Power /> : <PowerOff />} {on ? t('card.switchOn') : t('card.switchOff')}
        </Button>
      </DialogFooter>
    </div>
  );
}
